import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../../../../../../redux/store";
import { Link, Image } from "phosphor-react";
import { Bold, Italic } from "lucide-react";
import { fetchPosts, upsertPost } from "../../../../../../../redux/blogSlice";

const EditPost: React.FC = () => {
  const { postId } = useParams();
  const dispatch = useDispatch<AppDispatch>();
  const { posts } = useSelector((state: RootState) => state.blog);

  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [loading, setLoading] = useState(false);

  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const featuredInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchPosts());
  }, [dispatch]);

  useEffect(() => {
    const post = posts.find((p) => p.id === postId);
    if (post) {
      setTitle(post.title);
      setSlug(post.slug);
      setDescription(post.description || "");
      setContent(post.content || "");
      setFeaturedImage(post.featuredImage || "");
    }
  }, [postId, posts]);

  const handleUpdate = async () => {
    if (!title || !slug) return;
    setLoading(true);
    await dispatch(
      upsertPost({
        id: postId,
        title,
        slug,
        description,
        content,
        featuredImage,
      })
    );
    setLoading(false);
  };

  const format = (command: string) => {
    document.execCommand(command, false);
  };

  const insertLink = () => {
    const url = prompt("Enter URL");
    if (url) document.execCommand("createLink", false, url);
  };

  const insertImages = (files: FileList) => {
    const selection = window.getSelection();
    if (!selection || !selection.rangeCount) return;
    const range = selection.getRangeAt(0);
    Array.from(files).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = document.createElement("img");
        img.src = reader.result as string;
        img.style.maxWidth = "100%";
        img.style.borderRadius = "8px";
        img.style.margin = "8px 0";

        const currentRange = range.cloneRange(); // use a cloned range for each image
        currentRange.deleteContents();
        currentRange.insertNode(img);

        // Move cursor after inserted image
        currentRange.setStartAfter(img);
        currentRange.setEndAfter(img);
        selection.removeAllRanges();
        selection.addRange(currentRange);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFeaturedImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFeaturedImage(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Edit Post</h2>

      <label style={styles.label}>Title</label>
      <input
        style={styles.input}
        value={title}
        onChange={(e) => {
          setTitle(e.target.value);
          setSlug(e.target.value.toLowerCase().replace(/\s+/g, "-"));
        }}
      />

      <label style={styles.label}>Slug</label>
      <input
        style={styles.input}
        value={slug}
        onChange={(e) => setSlug(e.target.value)}
      />

      <label style={styles.label}>Description</label>
      <textarea
        style={styles.textarea}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      <label style={styles.label}>Featured Image</label>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => featuredInputRef.current?.click()}
          style={styles.selectButton}
        >
          Upload Featured Image
        </button>
        {featuredImage && (
          <img
            src={featuredImage}
            alt="Featured"
            style={{ height: 50, borderRadius: 6 }}
          />
        )}
      </div>
      <input
        type="file"
        accept="image/*"
        hidden
        ref={featuredInputRef}
        onChange={handleFeaturedImageSelect}
      />

      <label style={styles.label}>Article Content</label>
      <div style={styles.toolbar}>
        <Bold onClick={() => format("bold")} size={20} style={styles.icon} />
        <Italic onClick={() => format("italic")} size={20} style={styles.icon} />
        <Link onClick={insertLink} size={20} style={styles.icon} />
        <Image
          onClick={() => fileInputRef.current?.click()}
          size={20}
          style={styles.icon}
        />
      </div>

      <input
        type="file"
        accept="image/*"
        multiple
        hidden
        ref={fileInputRef}
        onChange={(e) => {
          if (e.target.files) insertImages(e.target.files);
        }}
      />

      <div
        contentEditable
        ref={editorRef}
        style={styles.editor}
        onInput={() => setContent(editorRef.current?.innerHTML || "")}
        dangerouslySetInnerHTML={{ __html: content }}
      />

      <button style={styles.button} onClick={handleUpdate} disabled={loading}>
        {loading ? "Updating..." : "Update Post"}
      </button>
    </div>
  );
};

export default EditPost;

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "2rem",
    background: "linear-gradient(160deg, #0a0014, #1e003a, #a78bfa, #111)",
    color: "#fff",
    minHeight: "100vh",
    fontFamily: "'Segoe UI', sans-serif",
    maxWidth: "800px",
    margin: "0 auto",
  },
  heading: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: "1.5rem",
    textAlign: "center",
  },
  label: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
    display: "block",
  },
  input: {
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #555",
    background: "#1e1e2f",
    color: "#fff",
    marginBottom: "1rem",
    width: "100%",
  },
  textarea: {
    padding: "10px",
    borderRadius: 8,
    border: "1px solid #555",
    background: "#1e1e2f",
    color: "#fff",
    minHeight: 100,
    marginBottom: "1rem",
    width: "100%",
  },
  toolbar: {
    display: "flex",
    gap: 12,
    marginBottom: 10,
  },
  icon: {
    cursor: "pointer",
    color: "#ccc",
  },
  editor: {
    minHeight: 300,
    border: "1px solid #555",
    padding: "1rem",
    borderRadius: 8,
    background: "#1e1e2f",
    color: "#fff",
    marginBottom: "1.5rem",
  },
  button: {
    background: "linear-gradient(to right, #a78bfa, #c084fc)",
    color: "#000",
    padding: "12px",
    border: "none",
    borderRadius: 8,
    fontWeight: "bold",
    cursor: "pointer",
    width: "100%",
    fontSize: 14,
  },
  selectButton: {
    padding: "8px 16px",
    background: "#333",
    color: "#fff",
    border: "1px solid #666",
    borderRadius: 6,
    cursor: "pointer",
  },
};
